/**
 * The one styles pattern for theme-aware screens (docs/DARK-MODE.md):
 *
 *   // styles.js — module-level StyleSheet.create becomes a factory:
 *   export const makeStyles = colors =>
 *     StyleSheet.create({container: {backgroundColor: colors.background}});
 *
 *   // index.js — inside the component:
 *   const styles = useThemedStyles(makeStyles);
 *
 * StyleSheet.create still runs (style validation in dev), but per theme
 * instead of at module load; the result is memoized per colors object, so a
 * theme flip re-creates styles exactly once and re-renders consumers.
 */
import {useMemo} from 'react';

import {useTheme} from './ThemeContext';
import type {ThemeColors} from './tokens';

export function useThemedStyles<T>(makeStyles: (colors: ThemeColors) => T): T {
  const {colors} = useTheme();
  // makeStyles factories are declared at module scope, so `colors` is the
  // only input that actually changes between renders.
  return useMemo(() => makeStyles(colors), [makeStyles, colors]);
}
